import { useState } from "react";

const ContactSection = () => {
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const [formOpen, setFormOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("sending");

    const form = e.currentTarget;
    const messageEl = form.querySelector("textarea[name=\"message\"]") as HTMLTextAreaElement;
    if (messageEl && messageEl.value.length > 500) {
      alert("문의 내용은 500자 이내로 입력해주세요.");
      setFormStatus("idle");
      return;
    }

    const formData = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (input.name && input.value) {
        formData.append(input.name, input.value);
      }
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d6vmbtdt4s7j0tv5ihe0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString()
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section id="contact" className="py-14 sm:py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d0c1e38dae7a2c45de306324d26940d1.jpg"
          alt="시공 현장"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gray-900/85"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Left Info */}
          <div className="text-white">
            <span className="text-orange-400 text-xs sm:text-sm font-bold tracking-widest uppercase block mb-3">
              — CONTACT US
            </span>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4">
              견적 문의 &<br />
              <span className="text-orange-500">기술 상담</span>
            </h2>
            <p className="hidden sm:block text-white/60 text-base leading-relaxed mb-8">
              현장 조건과 설계 요구사항을 알려주시면<br />
              전문 엔지니어가 최적의 솔루션을 제안해 드립니다.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              {[
                { icon: "ri-map-pin-2-line", label: "주소", value: "충북 음성군 삼성면 덕호로 543" },
                { icon: "ri-phone-line", label: "대표전화", value: "043) 883-8566" },
                { icon: "ri-smartphone-line", label: "휴대전화", value: "010-9107-7017 / 010-9481-8566" },
                { icon: "ri-mail-line", label: "이메일", value: "ssc8566@naver.com" },
              ].map((info) => (
                <div key={info.label} className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-orange-500/20 rounded-lg sm:rounded-xl shrink-0">
                    <i className={`${info.icon} text-orange-400 text-sm sm:text-base`}></i>
                  </div>
                  <div>
                    <div className="text-white/40 text-xs font-semibold uppercase tracking-wider">{info.label}</div>
                    <div className="text-white text-sm sm:text-base font-semibold">{info.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* 지도 앱 연결 버튼 */}
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  const appUrl = "tmap://route?goalname=%EC%82%BC%EC%84%B1%EC%BD%98%ED%81%AC%EB%A6%AC%ED%8A%B8&goalx=127.5928&goaly=36.9472";
                  const webUrl = "https://tmap.life/o1mJzBdW";
                  const start = Date.now();
                  window.location.href = appUrl;
                  setTimeout(() => {
                    if (Date.now() - start < 2000) {
                      window.open(webUrl, "_blank");
                    }
                  }, 1200);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold px-3 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-navigation-line text-sky-400 text-sm"></i>
                T맵으로 이동
              </button>
              <button
                onClick={() => {
                  const appUrl = "kakaomap://route?ep=36.9472,127.5928&by=CAR";
                  const webUrl = "https://map.kakao.com/link/to/%EC%82%BC%EC%84%B1%EC%BD%98%ED%81%AC%EB%A6%AC%ED%8A%B8,36.9472,127.5928";
                  const start = Date.now();
                  window.location.href = appUrl;
                  setTimeout(() => {
                    if (Date.now() - start < 2000) {
                      window.open(webUrl, "_blank");
                    }
                  }, 1200);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 bg-yellow-400/20 hover:bg-yellow-400/30 border border-yellow-400/30 text-white text-xs font-bold px-3 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-2-line text-yellow-400 text-sm"></i>
                카카오맵
              </button>
              <button
                onClick={() => {
                  const webUrl = "https://map.naver.com/v5/search/%EC%B6%A9%EB%B6%81%20%EC%9D%8C%EC%84%B1%EA%B5%B0%20%EC%82%BC%EC%84%B1%EB%A9%B4%20%EB%8D%95%ED%98%B8%EB%A1%9C%20543";
                  window.open(webUrl, "_blank");
                }}
                className="flex-1 flex items-center justify-center gap-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-white text-xs font-bold px-3 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-line text-green-400 text-sm"></i>
                네이버맵
              </button>
            </div>

            {/* Business Hours */}
            <div className="mt-5 sm:mt-8 bg-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-time-line text-orange-400 text-sm"></i>
                <span className="text-white font-bold text-xs sm:text-sm">운영 시간</span>
              </div>
              <div className="text-white/70 text-xs sm:text-sm space-y-1">
                <div className="flex justify-between">
                  <span>평일</span>
                  <span className="text-white font-semibold">07:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>토요일</span>
                  <span className="text-white font-semibold">07:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>일/공휴일</span>
                  <span className="text-white/40">휴무</span>
                </div>
              </div>
            </div>

            {/* 모바일 전용 - 폼 토글 버튼 */}
            <button
              onClick={() => setFormOpen(!formOpen)}
              className="sm:hidden mt-5 w-full flex items-center justify-between bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="flex items-center gap-2">
                <i className="ri-file-text-line text-base"></i>
                견적 문의서 작성하기
              </span>
              <i className={`ri-arrow-${formOpen ? "up" : "down"}-s-line text-lg transition-transform`}></i>
            </button>
          </div>

          {/* Right Form - 모바일: 토글, 데스크탑: 항상 표시 */}
          <div className={`${formOpen ? "block" : "hidden"} sm:block bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10`}>
            <h3 className="text-lg sm:text-2xl font-black text-gray-900 mb-4 sm:mb-6 flex items-center justify-between">
              견적 문의하기
              {/* 모바일 닫기 버튼 */}
              <button
                onClick={() => setFormOpen(false)}
                className="sm:hidden w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </h3>

            {formStatus === "success" ? (
              <div className="text-center py-8 sm:py-12">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-line text-green-500 text-2xl sm:text-3xl"></i>
                </div>
                <h4 className="text-base sm:text-xl font-black text-gray-900 mb-2">문의가 접수되었습니다</h4>
                <p className="text-gray-500 text-xs sm:text-sm">담당자가 확인 후 빠르게 연락드리겠습니다.</p>
                <button
                  onClick={() => { setFormStatus("idle"); setFormOpen(false); }}
                  className="mt-4 text-orange-500 font-bold text-sm cursor-pointer hover:underline"
                >
                  닫기
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">성함 *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="홍길동"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">연락처 *</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="010-0000-0000"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">이메일</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="example@company.com"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">관심 제품</label>
                  <select
                    name="product"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors bg-white cursor-pointer"
                  >
                    <option value="">제품 선택</option>
                    <option value="SC보강토 옹벽블록">SC보강토 옹벽블록</option>
                    <option value="SC옹벽 축조블록">SC옹벽 축조블록</option>
                    <option value="SC식생 호안 축조블록">SC식생 호안 축조블록</option>
                    <option value="세코 옹벽 블록">세코 옹벽 블록</option>
                    <option value="레디락 옹벽 블록">레디락 옹벽 블록</option>
                    <option value="복수 제품 / 미정">복수 / 미정</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    문의 내용 *{" "}
                    <span className={`font-normal ${charCount > 500 ? "text-red-500" : "text-gray-400"}`}>
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={3}
                    maxLength={500}
                    placeholder="현장 위치, 규모, 납기 일정 등을 입력해주세요."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
                  />
                </div>

                {formStatus === "error" && (
                  <p className="text-red-500 text-xs">전송에 실패했습니다. 다시 시도해주세요.</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "sending"}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold py-3 sm:py-4 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === "sending" ? "전송 중..." : "문의 접수하기"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
